<template>
    <EditComponent
        dialog-id="editCategoriesDialog"
        open-dialog-event="categories:edit-mode"
        updated-event="categories:updated"
        :open-dialog-post-function="initCategoriesAndPredictions"
        :confirm-function="update"
        :confirm-enabled="isValidToSubmit"
        :track-section-opened="EditionOpenedSection.CATEGORIES"
        :track-existing-element="true"
    >
        <template #edit-header>{{ t('profile.section.categories.title') }}</template>
        <template #edit-body>
            <CategorizationDetailsSkeleton v-if="loading" />

            <CategorizationPrediction
                v-if="hasPredictions && !loading"
                :categories="predictions"
                :selected-categories="[]"
                @input-family-category="selectFamilyAndCategory"
                @manual-selection="switchToManualSelection"
                @prediction-selection="predictionSelection"
            />

            <CategorizationManualSelection
                v-if="!hasPredictions && !loading"
                :families="families"
                :categories="selectedCategories"
                :had-predictions="predictions.length > 0"
                @input-family="manualSelectFamily"
                @input-category="manualSelectCategory"
                @other-category-change="manualSelectOtherCategory"
                @unselect-categories="manualUnselectCategories"
                @go-back="loadPredictions"
                @manual-selection="manualSelection"
            />
        </template>
    </EditComponent>
</template>

<script setup lang="ts">
    import {computed, reactive} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import type {PredictCategoryResource} from '@api';
    import CategorizationDetailsSkeleton from '~/components/edit/categories/CategorizationDetailsSkeleton.vue';
    import CategorizationManualSelection from '~/components/edit/categories/CategorizationManualSelection.vue';
    import CategorizationPrediction from '~/components/edit/categories/CategorizationPrediction.vue';
    import EditComponent from '~/components/edit/EditComponent.vue';
    import {FamiliesCategoriesEdition} from '~/composables/categories/FamiliesCategories';
    import {EditionOpenedSection} from '~/config/TrackingEvents';
    import {useCategoryStore} from '~/store/category/categoryStore';

    const {t} = useTranslation();
    const {loadPredictions, loadFamilies, unloadPredictions, save} = useCategoryStore();
    const {loading, predictions, families} = storeToRefs(useCategoryStore());

    const state = reactive<{
        familiesCategoriesEdition: FamiliesCategoriesEdition;
        predictionsKeeper: PredictCategoryResource[];
    }>({
        familiesCategoriesEdition: new FamiliesCategoriesEdition(),
        predictionsKeeper: [],
    });

    const isValidToSubmit = computed(() => state.familiesCategoriesEdition.hasCompleteSelection());
    const hasPredictions = computed(() => predictions.value.length > 0);
    const selectedCategories = computed(() => state.familiesCategoriesEdition.allSelectedCategories());

    async function initCategoriesAndPredictions(): Promise<void> {
        await loadPredictions();
        await loadFamilies();
    }

    function selectFamilyAndCategory(familyAndCategory: any): void {
        const action = familyAndCategory.action;
        const family = familyAndCategory.familyId as string;
        const category = familyAndCategory.categoryId as string;

        if (action === 'unselect') {
            state.familiesCategoriesEdition.predicted.unSelectCategory(family, category, families.value);
        } else {
            state.familiesCategoriesEdition.predicted.selectCategory(family, category);
        }
    }

    async function switchToManualSelection(): Promise<void> {
        if (families.value.length === 0) {
            await loadFamilies();
        }
        state.predictionsKeeper = [...predictions.value];
        unloadPredictions();
    }

    function manualSelection(): void {
        state.familiesCategoriesEdition.switchToManual();
    }

    function predictionSelection(): void {
        state.familiesCategoriesEdition.switchToPrediction();
    }

    function manualSelectFamily(familyId: string): void {
        state.familiesCategoriesEdition.manual.selectFamily(familyId);
    }

    function manualSelectCategory(categories: string[]): void {
        state.familiesCategoriesEdition.manual.selectCategories(categories);
    }

    function manualSelectOtherCategory(otherCategory: string): void {
        state.familiesCategoriesEdition.manual.selectOtherCategory(otherCategory);
    }

    function manualUnselectCategories(): void {
        state.familiesCategoriesEdition.manual.resetSelection();
    }

    async function update(): Promise<void> {
        await save(state.familiesCategoriesEdition.toRequest());
    }
</script>
